import { isSuccess, type IHTTPResponse } from '@yimoka/shared';
import { Entity } from '@yimoka/store';
import { encrypt as safeEncrypt } from '@yimoko/safe';

import {
  platformApiExecutor,
  platformHttpGet,
  platformHttpPost,
  tenantHttpGet,
  tenantHttpPost,
} from '@/http';
import { getClientID, setClientID } from '@/session/auth-session';
import { decodeOAuthStateContext } from '@/utils/oauth-state';

import type {
  IAuthAccount,
  IAuthAccountAuthConfig,
  IAuthAccountImageCaptcha,
  IAuthAccountMenuApp,
  IAuthAccountMenuItem,
  IAuthAccountMenuParams,
  IAuthAccountMyPhoneCaptchaParams,
  IAuthAccountOAuth2CallbackValues,
  IAuthAccountOAuth2LoginByCodeParams,
  IAuthAccountOIDCChallengeParams,
  IAuthAccountOIDCConsentParams,
  IAuthAccountOIDCRedirect,
  IAuthAccountPasswordLoginParams,
  IAuthAccountPasswordLoginValues,
  IAuthAccountPhoneCaptchaParams,
  IAuthAccountPhoneLoginParams,
  IAuthAccountPhoneLoginValues,
  IAuthAccountStaff,
  IAuthAccountStaffAuthReply,
  IAuthAccountStaffInfoValues,
  IAuthAccountTenant,
  IAuthAccountTenantLoginParams,
  IAuthAccountUpdatePasswordParams,
  IAuthAccountUpdatePasswordValues,
  IAuthAccountUserAuthReply,
  IAuthAccountVerifyInviteParams,
} from './auth-account.d';

const OP_AUTH_CONFIG = 'authConfig';
const OP_USER_INFO = 'userInfo';
const OP_PASSWORD_LOGIN = 'passwordLogin';
const OP_PHONE_LOGIN = 'phoneLogin';
const OP_PHONE_CAPTCHA = 'phoneCaptcha';
const OP_OAUTH2_LOGIN = 'oauth2Login';
const OP_TENANT_STAFFS = 'tenantStaffs';
const OP_TENANT_LOGIN = 'tenantLogin';
const OP_STAFF_INFO = 'staffInfo';
const OP_TENANT_INFO = 'tenantInfo';
const OP_MY_MENUS = 'myMenus';
const OP_MY_PHONE_CAPTCHA = 'myPhoneCaptcha';
const OP_UPDATE_PASSWORD = 'updatePassword';
const OP_VERIFY_INVITE = 'verifyInvite';
const OP_OIDC_ACCEPT_LOGIN = 'oidcAcceptLogin';
const OP_OIDC_ACCEPT_CONSENT = 'oidcAcceptConsent';
const OP_OIDC_ACCEPT_LOGOUT = 'oidcAcceptLogout';
const OP_PLATFORM_LOGOUT = 'platformLogout';
const OP_TENANT_LOGOUT = 'tenantLogout';

let serverTimeDiff: number | null = null;

/** authAccountGetAuthConfig 读取 BaseUser 统一维护的注册、登录与第三方认证公开配置。 */
export const authAccountGetAuthConfig = () => (
  platformHttpGet<IAuthAccountAuthConfig, Record<string, never>>('/base/user/portal/auth/config')
);

export const authAccountCreateID = () => (
  platformHttpPost<string, Record<string, never>>('/base/user/portal/id/create', {})
);

export const authAccountGetTimestamp = () => (
  platformHttpGet<string, Record<string, never>>('/base/user/portal/timestamp')
);

export const authAccountGetPhoneCaptcha = (params: IAuthAccountPhoneCaptchaParams) => (
  platformHttpPost<boolean, IAuthAccountPhoneCaptchaParams>('/base/user/portal/captcha/phone', params)
);

export const authAccountGetImageCaptcha = () => (
  platformHttpGet<IAuthAccountImageCaptcha, Record<string, never>>('/base/user/portal/captcha/image')
);

export const authAccountLoginByPhone = (values: IAuthAccountPhoneLoginValues) => {
  const params = { ...values };
  delete params.agreement;
  return platformHttpPost<IAuthAccountUserAuthReply, IAuthAccountPhoneLoginParams>(
    '/base/user/portal/login/phone',
    params,
  );
};

function isSafeClientID(clientID: string) {
  return /^0[0-9a-zA-Z]{31}$/.test(clientID);
}

async function ensureSafeClientID() {
  const current = getClientID();
  if (isSafeClientID(current)) return current;

  const response = await authAccountCreateID();
  if (isSuccess(response) && response.data) {
    setClientID(response.data);
    return response.data;
  }

  return current;
}

async function getSafeTimestamp() {
  const localTime = Math.floor(Date.now() / 1000);

  if (serverTimeDiff !== null && Math.abs(serverTimeDiff) <= 120) {
    return localTime + serverTimeDiff;
  }

  const response = await authAccountGetTimestamp();
  if (isSuccess(response) && response.data) {
    const serverTime = Number(response.data);
    if (Number.isFinite(serverTime)) {
      serverTimeDiff = serverTime - localTime;
      return serverTime;
    }
  }

  serverTimeDiff = 0;
  return localTime;
}

/**
 * encryptAccountPassword 复用登录动作的 base-safe 加密协议，兼容后端对登录、初始化和修改密码的统一解密约定。
 */
async function encryptAccountPassword(value: string) {
  const id = await ensureSafeClientID();
  const timestamp = await getSafeTimestamp();

  return safeEncrypt(value, {
    id,
    timestamp,
    action: 'login',
    preventReplay: true,
  });
}

export const authAccountLogin = async (
  values: IAuthAccountPasswordLoginValues,
): Promise<IHTTPResponse<IAuthAccountUserAuthReply, IAuthAccountPasswordLoginValues>> => {
  const { password, ...rest } = values;
  delete rest.agreement;
  const params: IAuthAccountPasswordLoginParams = {
    ...rest,
    password: await encryptAccountPassword(password),
  };

  return platformHttpPost<IAuthAccountUserAuthReply, IAuthAccountPasswordLoginParams>(
    '/base/user/portal/login',
    params,
  ) as Promise<IHTTPResponse<IAuthAccountUserAuthReply, IAuthAccountPasswordLoginValues>>;
};

export const authAccountGetMyInfo = () => (
  platformHttpGet<IAuthAccountUserAuthReply, never>('/base/user/portal/my/info')
);

export const authAccountThirdOAuthLoginByCode = (
  params: IAuthAccountOAuth2LoginByCodeParams,
) => (
  platformHttpPost<IAuthAccountUserAuthReply, IAuthAccountOAuth2LoginByCodeParams>(
    '/base/user/portal/oauth/third/loginByCode',
    params,
  )
);

/** authAccountAcceptOIDCLogin 接受 Hydra 登录 challenge，并返回授权服务器下一跳。 */
export const authAccountAcceptOIDCLogin = (params: IAuthAccountOIDCChallengeParams) => (
  platformHttpPost<IAuthAccountOIDCRedirect, IAuthAccountOIDCChallengeParams>(
    '/base/user/portal/oauth/acceptLogin',
    params,
  )
);

/** authAccountAcceptOIDCConsent 接受 Hydra consent challenge，沿用旧 Web 的 openid 与记住授权策略。 */
export const authAccountAcceptOIDCConsent = (params: IAuthAccountOIDCConsentParams) => (
  platformHttpPost<IAuthAccountOIDCRedirect, IAuthAccountOIDCConsentParams>(
    '/base/user/portal/oauth/acceptConsent',
    params,
  )
);

/** authAccountAcceptOIDCLogout 接受 Hydra logout challenge，并返回登出完成地址。 */
export const authAccountAcceptOIDCLogout = (params: IAuthAccountOIDCChallengeParams) => (
  platformHttpPost<IAuthAccountOIDCRedirect, IAuthAccountOIDCChallengeParams>(
    '/base/user/portal/oauth/acceptLogout',
    params,
  )
);

/** authAccountLogout 退出当前平台用户登录态，不影响后端既有会话协议。 */
export const authAccountLogout = () => (
  platformHttpPost<boolean, Record<string, never>>('/base/user/portal/logout', {})
);

/** authAccountLogoutTenant 退出当前租户员工会话，并跳过 401 自动续期以免登出请求重新登录。 */
export const authAccountLogoutTenant = () => (
  tenantHttpPost<boolean, Record<string, never>>(
    '/base/iam/portal/logout',
    {},
    { tenantAuthRecovery: 'skip' },
  )
);

/** findOAuth2ProviderByCallbackURL 只在回调地址唯一匹配时兼容缺失 state，避免共用回调地址时串错 provider。 */
function findOAuth2ProviderByCallbackURL(
  oauth2List: IAuthAccountOAuth2CallbackValues['oauth2List'],
  callbackURL?: string,
) {
  if (!callbackURL) return undefined;
  try {
    const current = new URL(callbackURL);
    const currentPrefix = `${current.origin}${current.pathname}`;
    const matches = oauth2List?.filter(item => (item.redirectURLs ?? []).some((redirectURL) => {
      try {
        const redirect = new URL(redirectURL);
        return `${redirect.origin}${redirect.pathname}` === currentPrefix;
      } catch {
        return false;
      }
    }));
    return matches?.length === 1 ? matches[0] : undefined;
  } catch {
    return undefined;
  }
}

/** findOAuth2ProviderByState 使用 provider 与 clientID 精确定位，新字段缺失时仅兼容唯一 clientID。 */
function findOAuth2ProviderByState(
  oauth2List: IAuthAccountOAuth2CallbackValues['oauth2List'],
  stateContext: ReturnType<typeof decodeOAuthStateContext>,
) {
  const matches = oauth2List?.filter(item => item.clientID === stateContext.clientID) ?? [];
  if (stateContext.provider) {
    return matches.find(item => item.provider === stateContext.provider);
  }
  return matches.length === 1 ? matches[0] : undefined;
}

/** authAccountOAuth2CallbackLogin 兼容新版上下文、旧版 clientID 和缺失 state 的 Web OAuth 回调。 */
export const authAccountOAuth2CallbackLogin = async (
  values: IAuthAccountOAuth2CallbackValues,
): Promise<IHTTPResponse<IAuthAccountUserAuthReply, IAuthAccountOAuth2CallbackValues>> => {
  const {
    callbackURL = typeof window === 'undefined' ? '' : window.location.href,
    code = '',
    state = '',
    oauth2List = [],
  } = values;
  if (!code) {
    return { code: 400, data: {}, msg: 'missing_oauth_code' };
  }

  // 新 state 精确恢复 provider+clientID；旧 state 只在 clientID 唯一时兼容，不能按列表首项猜 provider。
  const stateContext = decodeOAuthStateContext(state);
  const provider = findOAuth2ProviderByState(oauth2List, stateContext)
    ?? (!state ? findOAuth2ProviderByCallbackURL(oauth2List, callbackURL) : undefined);
  if (!provider) {
    return { code: 404, data: {}, msg: 'missing_oauth_provider' };
  }

  const redirectURLs = provider.redirectURLs ?? [];
  const redirectURL = redirectURLs.find((value) => {
    try {
      const current = new URL(callbackURL);
      const redirect = new URL(value);
      return current.origin === redirect.origin && current.pathname === redirect.pathname;
    } catch {
      return false;
    }
  }) ?? redirectURLs[0];
  if (!redirectURL) {
    return { code: 422, data: {}, msg: 'missing_oauth_redirect_url' };
  }

  return authAccountThirdOAuthLoginByCode({
    clientID: provider.clientID,
    provider: provider.provider,
    code,
    redirectURL,
    state: provider.clientID,
  }) as Promise<IHTTPResponse<IAuthAccountUserAuthReply, IAuthAccountOAuth2CallbackValues>>;
};

/** authAccountVerifyInviteLink 使用当前平台用户手机号验证邀请并绑定目标员工。 */
export const authAccountVerifyInviteLink = (params: IAuthAccountVerifyInviteParams) => (
  platformHttpPost<boolean, IAuthAccountVerifyInviteParams>(
    '/base/iam/portal/staff/verifyInviteLink',
    params,
  )
);

/** authAccountGetUserBindStaffs 读取当前平台用户绑定的租户员工身份列表。 */
export const authAccountGetUserBindStaffs = () => (
  platformHttpGet<IAuthAccountStaff[], never>('/base/iam/portal/staff/userBind')
);

export const authAccountLoginByUser = (params: IAuthAccountTenantLoginParams) => (
  platformHttpPost<IAuthAccountStaffAuthReply, IAuthAccountTenantLoginParams>(
    '/base/iam/portal/loginByUser',
    params,
  )
);

/** authAccountGetStaffInfo 获取当前员工资料，并允许首次租户入口独占自身的 401 恢复状态机。 */
export const authAccountGetStaffInfo = (values?: IAuthAccountStaffInfoValues) => (
  tenantHttpGet<IAuthAccountStaffAuthReply, never>('/base/iam/portal/staff/info', {
    tenantAuthRecovery: values?.skipTenantAuthRecovery ? 'skip' : undefined,
  })
);

/** authAccountGetMyPhoneCaptcha 向当前平台用户已绑定的安全手机发送验证码。 */
export const authAccountGetMyPhoneCaptcha = (params: IAuthAccountMyPhoneCaptchaParams) => (
  platformHttpPost<boolean, IAuthAccountMyPhoneCaptchaParams>(
    '/base/user/portal/my/captcha/phone',
    params,
  )
);

/**
 * authAccountUpdatePassword 在协议边界加密新密码，Store 和页面只处理修改密码的业务原值。
 */
export const authAccountUpdatePassword = async (
  values: IAuthAccountUpdatePasswordValues,
): Promise<IHTTPResponse<boolean, IAuthAccountUpdatePasswordValues>> => {
  const params: IAuthAccountUpdatePasswordParams = {
    code: values.code,
    newPassword: await encryptAccountPassword(values.newPassword),
  };

  return platformHttpPost<boolean, IAuthAccountUpdatePasswordParams>(
    '/base/user/portal/my/password/update',
    params,
  ) as Promise<IHTTPResponse<boolean, IAuthAccountUpdatePasswordValues>>;
};

export const authAccountGetTenantInfo = () => (
  tenantHttpGet<IAuthAccountTenant, never>('/base/iam/portal/tenant/info')
);

/** authAccountGetMyMenus 按应用权限标识读取当前员工可见菜单。 */
export const authAccountGetMyMenus = ({ app }: IAuthAccountMenuParams) => (
  tenantHttpGet<IAuthAccountMenuItem[], IAuthAccountMenuParams>('/base/iam/portal/my/menu', {
    // 工作台、个人中心和管理台分别以 workspace/personal/console 作为权限应用标识。
    headers: { 'x-md-global-app': app },
  })
);

/** AuthAccountEntity 统一管理登录、租户会话和账号安全相关的原子 Store。 */
export class AuthAccountEntity extends Entity<IAuthAccount> {
  constructor() {
    super({
      name: 'AuthAccount',
      idKey: 'id',
      apiExecutor: platformApiExecutor,
    });
  }

  /** authConfigStore 管理 BaseUser 公开认证配置的读取状态。 */
  get authConfigStore() {
    return this.getOperation<Record<string, never>, IAuthAccountAuthConfig>({
      opType: OP_AUTH_CONFIG,
      api: authAccountGetAuthConfig,
      defaultValues: {},
    });
  }

  /** userInfoStore 校验并刷新当前平台用户登录态。 */
  get userInfoStore() {
    return this.getOperation<Record<string, never>, IAuthAccountUserAuthReply>({
      opType: OP_USER_INFO,
      api: authAccountGetMyInfo,
    });
  }

  get passwordLoginStore() {
    return this.getOperation<IAuthAccountPasswordLoginValues, IAuthAccountUserAuthReply>({
      opType: OP_PASSWORD_LOGIN,
      api: authAccountLogin,
      defaultValues: { name: '', password: '', phonePrefix: '86', agreement: false },
    });
  }

  get phoneLoginStore() {
    return this.getOperation<IAuthAccountPhoneLoginValues, IAuthAccountUserAuthReply>({
      opType: OP_PHONE_LOGIN,
      api: authAccountLoginByPhone,
      defaultValues: { phonePrefix: '86', phone: '', code: '', agreement: false },
    });
  }

  get phoneCaptchaStore() {
    return this.getOperation<IAuthAccountPhoneCaptchaParams, boolean>({
      opType: OP_PHONE_CAPTCHA,
      api: authAccountGetPhoneCaptcha,
      defaultValues: { phonePrefix: '86', phone: '' },
    });
  }

  get oauth2LoginStore() {
    return this.getOperation<IAuthAccountOAuth2CallbackValues, IAuthAccountUserAuthReply>({
      opType: OP_OAUTH2_LOGIN,
      api: authAccountOAuth2CallbackLogin,
      defaultValues: { code: '', state: '', oauth2List: [] },
    });
  }

  /** oidcAcceptLoginStore 承载 Hydra 登录 challenge 的一次性接受操作。 */
  get oidcAcceptLoginStore() {
    return this.getOperation<IAuthAccountOIDCChallengeParams, IAuthAccountOIDCRedirect>({
      opType: OP_OIDC_ACCEPT_LOGIN,
      api: authAccountAcceptOIDCLogin,
      defaultValues: { challenge: '' },
    });
  }

  /** oidcAcceptConsentStore 承载 Hydra 授权确认的一次性接受操作。 */
  get oidcAcceptConsentStore() {
    return this.getOperation<IAuthAccountOIDCConsentParams, IAuthAccountOIDCRedirect>({
      opType: OP_OIDC_ACCEPT_CONSENT,
      api: authAccountAcceptOIDCConsent,
      defaultValues: { challenge: '', scope: ['openid'], remember: true },
    });
  }

  /** oidcAcceptLogoutStore 承载 Hydra 登出 challenge 的一次性接受操作。 */
  get oidcAcceptLogoutStore() {
    return this.getOperation<IAuthAccountOIDCChallengeParams, IAuthAccountOIDCRedirect>({
      opType: OP_OIDC_ACCEPT_LOGOUT,
      api: authAccountAcceptOIDCLogout,
      defaultValues: { challenge: '' },
    });
  }

  /** platformLogoutStore 承载登出成功页的当前平台账号退出操作。 */
  get platformLogoutStore() {
    return this.getOperation<Record<string, never>, boolean>({
      opType: OP_PLATFORM_LOGOUT,
      api: authAccountLogout,
      defaultValues: {},
    });
  }

  /** tenantLogoutStore 承载当前单位员工会话退出操作。 */
  get tenantLogoutStore() {
    return this.getOperation<Record<string, never>, boolean>({
      opType: OP_TENANT_LOGOUT,
      api: authAccountLogoutTenant,
      defaultValues: {},
    });
  }

  /** inviteVerifyStore 提交邀请绑定，手机号必须来自已校验的平台用户资料。 */
  get inviteVerifyStore() {
    return this.getOperation<IAuthAccountVerifyInviteParams, boolean>({
      opType: OP_VERIFY_INVITE,
      api: authAccountVerifyInviteLink,
      defaultValues: { phone: '', phonePrefix: '', tenantID: '', token: '' },
    });
  }

  get tenantStaffsStore() {
    return this.getOperation<Record<string, never>, IAuthAccountStaff[]>({
      opType: OP_TENANT_STAFFS,
      api: authAccountGetUserBindStaffs,
    });
  }

  /** tenantLoginStore 保留默认租户登录 Store 的兼容入口。 */
  get tenantLoginStore() {
    return this.getTenantLoginStore();
  }

  /** getTenantLoginStore 按认证流程隔离租户登录请求，避免快速切换租户时共享参数和响应。 */
  getTenantLoginStore(instanceKey = 'default') {
    return this.getOperation<IAuthAccountTenantLoginParams, IAuthAccountStaffAuthReply>({
      opType: OP_TENANT_LOGIN,
      instanceKey,
      api: authAccountLoginByUser,
      defaultValues: { staffID: '', tenantID: '' },
    });
  }

  /** staffInfoStore 保留工作台壳层读取默认员工资料 Store 的兼容入口。 */
  get staffInfoStore() {
    return this.getStaffInfoStore();
  }

  /** getStaffInfoStore 按页面实例隔离员工资料请求，避免账号安全页卸载影响工作台壳层。 */
  getStaffInfoStore(instanceKey = 'default', defaultValues: IAuthAccountStaffInfoValues = {}) {
    return this.getOperation<IAuthAccountStaffInfoValues, IAuthAccountStaffAuthReply>({
      opType: OP_STAFF_INFO,
      instanceKey,
      api: authAccountGetStaffInfo,
      defaultValues,
    });
  }

  /** getMyPhoneCaptchaStore 返回当前用户安全手机验证码操作 Store。 */
  getMyPhoneCaptchaStore(instanceKey = 'default') {
    return this.getOperation<IAuthAccountMyPhoneCaptchaParams, boolean>({
      opType: OP_MY_PHONE_CAPTCHA,
      instanceKey,
      api: authAccountGetMyPhoneCaptcha,
      defaultValues: {},
    });
  }

  /** getUpdatePasswordStore 返回验证码修改登录密码操作 Store。 */
  getUpdatePasswordStore(instanceKey = 'default') {
    return this.getOperation<IAuthAccountUpdatePasswordValues, boolean>({
      opType: OP_UPDATE_PASSWORD,
      instanceKey,
      api: authAccountUpdatePassword,
      defaultValues: { code: '', newPassword: '' },
    });
  }

  /** disposeAccountSecurityStores 释放账号安全页专属请求状态和敏感表单值。 */
  disposeAccountSecurityStores(instanceKey: string) {
    this.disposeOperation(OP_STAFF_INFO, instanceKey);
    this.disposeOperation(OP_MY_PHONE_CAPTCHA, instanceKey);
    this.disposeOperation(OP_UPDATE_PASSWORD, instanceKey);
  }

  get tenantInfoStore() {
    return this.getTenantInfoStore('current');
  }

  getTenantInfoStore(tenantID: string) {
    return this.getOperation<Record<string, never>, IAuthAccountTenant>({
      opType: OP_TENANT_INFO,
      instanceKey: tenantID || 'unknown',
      api: authAccountGetTenantInfo,
    });
  }

  getMyMenusStore(tenantID: string, app: IAuthAccountMenuApp) {
    return this.getOperation<IAuthAccountMenuParams, IAuthAccountMenuItem[]>({
      opType: OP_MY_MENUS,
      instanceKey: `${tenantID || 'unknown'}:${app}`,
      api: authAccountGetMyMenus,
      defaultValues: { app },
    });
  }
}

export const authAccountEntity = new AuthAccountEntity();
